import {
	ReactElement,
	useEffect,
	useState
} from "react";
import { DimensionToRender } from "../../../RenderState/common";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	addDays,
	formatForDto,
	getStartOfDay,
	getStartOfWeek
} from "client/DateAndTime/dateTimeUtils";
import { never } from "client_legacy/ndaw/errors";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { useForceRerender } from "client/Hooks";
import {
	IntegrationTileCalendarViewKind,
	IntegrationTileLayoutKind,
	PlanningIntegrationTileFlowSettingsModel
} from "client/Api/Workspace/workspace";
import {
	getPlanningLiveInformation,
	PlanningIntegrationFlowLiveInformationResultStatus,
	PlanningIntegrationFlowResult,
	PlanningIntegrationFlowResultItemModel
} from "client/Api/Workspace/LiveInformation/Integration/integrationLiveInformation";
import { IntegrationFlowMultipleAuthenticationsAreRequiredModel } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { PlanningDaySchedule } from "./PlanningDaySchedule";
import { PlanningWeekSchedule } from "./PlanningWeekSchedule";

export type PlanningLiveTileContentProps = {
	connectionId: number;
	appName: string;
	dimensionToRender: DimensionToRender;
	flowSettings: PlanningIntegrationTileFlowSettingsModel;
	userTimezone: TimeZone;
};

export const PlanningLiveTileContent = (props: PlanningLiveTileContentProps): ReactElement => {
	const {
		connectionId,
		appName,
		dimensionToRender,
		flowSettings,
		userTimezone
	} = props;

	const { layout } = flowSettings;

	if (layout.kind === IntegrationTileLayoutKind.List) {
		throw new Error("The list layout is not supported yet.");
	}

	const [date, setDate] = useState<Date>(() => new Date());

	const [view, setView] = useState<IntegrationTileCalendarViewKind>(layout.defaultView);

	const [state, setState] = useState<PlanningLiveInformationState>({
		status: "isLoading"
	});

	const [reload, reloadVersion] = useForceRerender();

	const isTodayButtonAvailable: boolean = dimensionToRender.dimension
		.columnSpan === 8;

	const isWeekScheduleAvailable: boolean = dimensionToRender.dimension
		.columnSpan >= 6;

	const visibleView: IntegrationTileCalendarViewKind = isWeekScheduleAvailable
		? view
		: IntegrationTileCalendarViewKind.Day;

	const {
		startDateTime,
		endDateTime
	} = getDateRange(
		date,
		visibleView,
		userTimezone
	);

	const requestStartDateTime: string = formatForDto(startDateTime, userTimezone);

	const requestEndDateTime: string = formatForDto(endDateTime, userTimezone);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(reloadVersion);

			setState({
				status: "isLoading"
			});

			const load = async (signal: AbortSignal): Promise<void> => {
				try {
					const result: PlanningIntegrationFlowResult = await getPlanningLiveInformation(
						connectionId,
						{
							startDateTime: requestStartDateTime,
							endDateTime: requestEndDateTime
						},
						signal
					);

					switch (result.status) {
						case PlanningIntegrationFlowLiveInformationResultStatus.Failure:
							setState({
								status: "globalFailure"
							});
							break;
						case PlanningIntegrationFlowLiveInformationResultStatus.AuthenticationRequired:
							setState({
								status: "authenticationRequired",
								authenticationModel: result.authenticationModel
							});
							break;
						case PlanningIntegrationFlowLiveInformationResultStatus.Success:
							setState({
								status: "loaded",
								items: result.items
									.map(
										x => ({
											...x,
											uniqueId: newGuid()
										})
									)
							});
							break;
						default:
							never(result);
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					setState({
						status: "globalFailure"
					});
				}
			};

			const cancellation = new AbortController();

			load(cancellation.signal);

			return (): void => {
				cancellation.abort();
			};
		},
		[
			reloadVersion,
			connectionId,
			requestStartDateTime,
			requestEndDateTime
		]
	);

	switch (visibleView) {
		case IntegrationTileCalendarViewKind.Week:
			return (
				<PlanningWeekSchedule
					state={state}
					onReload={reload}
					appName={appName}
					date={date}
					onDateChange={setDate}
					isTodayButtonAvailable={isTodayButtonAvailable}
					timezone={userTimezone}
					onViewChange={setView}
					showHoursColumn={layout.showHoursColumn}
				/>
			);
		case IntegrationTileCalendarViewKind.Day:
			return (
				<PlanningDaySchedule
					state={state}
					onReload={reload}
					appName={appName}
					date={date}
					onDateChange={setDate}
					isTodayButtonAvailable={isTodayButtonAvailable}
					timezone={userTimezone}
					isViewSwitchAvailable={isWeekScheduleAvailable}
					onViewChange={setView}
				/>
			);
		default:
			return never(visibleView);
	}
};
PlanningLiveTileContent.displayName = "PlanningLiveTileContent";

export type PlanningItemModel = PlanningIntegrationFlowResultItemModel & {
	uniqueId: Guid;
};

export type PlanningLiveInformationState = {
	status: "isLoading" | "globalFailure";
} | {
	status: "authenticationRequired";
	authenticationModel: IntegrationFlowMultipleAuthenticationsAreRequiredModel;
} | {
	status: "loaded";
	items: readonly PlanningItemModel[];
};

type DateRange = {
	startDateTime: Date;
	endDateTime: Date;
};

function getDateRange(
	date: Date,
	view: IntegrationTileCalendarViewKind,
	timezone: TimeZone
): DateRange {
	switch (view) {
		case IntegrationTileCalendarViewKind.Week: {
			const startOfWeek: Date = getStartOfWeek(date, timezone);

			return {
				startDateTime: startOfWeek,
				endDateTime: addDays(
					startOfWeek,
					7,
					timezone
				)
			};
		}
		case IntegrationTileCalendarViewKind.Day: {
			const startOfDay: Date = getStartOfDay(date, timezone);

			return {
				startDateTime: startOfDay,
				endDateTime: addDays(
					startOfDay,
					1,
					timezone
				)
			};
		}
		default:
			return never(view);
	}
}
